import React, { useRef, useState } from "react";
import { Space } from "antd";
import styles from "./style.module.scss";
import VLPopover from "../VLPopover";
import VLFloatButton from "../Buttons/VLFloatButton";
import MultiSelect from "views/uikit/FormFields/SelectList";
import { ActionItem } from "views/pages/DataPage/CardsView/DataList";

export interface VLFloatGroupProps {
  actionList: ActionItem[];
  placement?: "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  buttonSize?: "medium" | "large";
}

const VLFloatGroup: React.FC<VLFloatGroupProps> = ({
  actionList,
  placement = "topRight",
  tooltipPlacement = "top",
  buttonSize = "medium",
}) => {
  // Track which popover is open to control tooltip visibility
  const [openPopoverIndex, setOpenPopoverIndex] = useState<number | null>(null);

  const popupRootRef = useRef<HTMLDivElement>(null);
  const mountPopupsInGroup = () => popupRootRef.current ?? document.body;

  const [selectOpen, setSelectOpen] = useState(false);

  /** Close MultiSelect first, then popover (matches outside-click and onChange teardown). */
  const closeSelectThenPopover = () => {
    setSelectOpen(false);
    setTimeout(() => {
      setOpenPopoverIndex(null);
    }, 0);
  };

  const handlePopoverOpenChange = (open: boolean, index: number) => {
    if (open) {
      setOpenPopoverIndex(index);
    } else {
      closeSelectThenPopover();
    }
  };

  const handleOnChange = (value: string[], item: ActionItem) => {
    item.subAction?.onChange?.(value);
    closeSelectThenPopover();
  };

  return (
    <div
      ref={popupRootRef}
      className={styles.actionList}
      data-testid="vl-float-group"
    >
      <Space>
        {actionList.map((item: ActionItem, actionIndex: number) => (
          <div
            key={actionIndex}
            className={styles.actionItem}
            data-testid={`vl-float-group-item-${actionIndex}`}
            onClick={(e) => e.stopPropagation()}
          >
            <VLPopover
              getPopupContainer={mountPopupsInGroup}
              afterOpenChange={(open) => setSelectOpen(open)}
              placement={tooltipPlacement}
              trigger="click"
              open={openPopoverIndex === actionIndex}
              onOpenChange={(open) =>
                handlePopoverOpenChange(open, actionIndex)
              }
              content={
                item.subAction ? (
                  <div
                    className={styles.multiSelectDropdown}
                    data-testid="vl-float-group-multiselect-wrapper"
                    onClick={(e) => e.stopPropagation()} // clicks inside dropdown
                  >
                    <MultiSelect
                      mode="multiple"
                      options={item.subAction.options}
                      placement={placement}
                      placeholder="Please select"
                      onChange={(value) => handleOnChange(value, item)}
                      open={selectOpen}
                      onDropdownVisibleChange={(open) => setSelectOpen(open)}
                      getPopupContainer={mountPopupsInGroup}
                      autoFocus={false}
                    />
                  </div>
                ) : undefined
              }
            >
              <VLFloatButton
                icon={item.action.icon}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  // Only trigger main action if there is no subAction
                  if (!item.subAction) {
                    e.stopPropagation();
                    item.action.onClick?.(e);
                  }
                }}
                color={item.action.color}
                tooltip={openPopoverIndex !== actionIndex ? item.tooltip : ""}
                tooltipPlacement={tooltipPlacement}
                buttonSize={buttonSize}
              />
            </VLPopover>
          </div>
        ))}
      </Space>
    </div>
  );
};

export default VLFloatGroup;
